import { useForm } from '@tanstack/react-form'
import clsx from 'clsx'
import React, { ReactNode, useContext } from 'react'

import { ampli } from '@Analytics/ampli'
import CheckRoundedSVG from '@Assets/SVG/CheckRounded'
import { SVGIcon } from '@Components/Icons/SVGIcon'
import { InputErrors, InputGroup, InputGroupBase, InputGroupInput, InputGroupLabel, metaToFieldState } from '@Components/Input'
import { RTEContent } from '@Components/RTEContent'
import { Button } from '@CoreComponents/Buttons/Button'
import { gtm } from '@Globals/Google/DataLayer'
import { MetadataContext } from '@Globals/Metadata'
import { useNewsletterSubscription } from '@Globals/Newsletter/useNewsletterSubscription'
import { useTranslation } from '@Globals/Translations'

import styles from './SubscribeNewsletterForm.module.scss'

const EMAIL_REGEX = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

export const Subscribed = ({
  className,
  id,
  message,
}: {
  className?: string
  id?: string
  message?: string
}) => {
  const { t } = useTranslation()
  const text = message || t('cms:GREAT_WE_WILL_NOTIFY_YOU')

  return (
    <div className={clsx(styles.subscribed, className)} id={id}>
      <SVGIcon icon={CheckRoundedSVG} size="47px" />
      <div className={styles.subscribedContent}>
        {text && <RTEContent html={text} />}
      </div>
    </div>
  )
}

const UnsubscribePolicy = () => {
  const { t, tDanger } = useTranslation()
  const { cmsData } = useContext(MetadataContext)

  return (
    <p className={styles.policy}>
      <span
        {...tDanger(
          'footer:I_AM_AWARE_OF_TRENDHIM_PRIVACY_POLICY',
          {
            arg1: cmsData?.urls?.privacyPolicy?.url,
          },
        )}
      />
      .
      {' '}
      {t('footer:UNSUBSCRIBE_AT_ANY_TIME')}
      .
    </p>
  )
}

export const SubscribeNewsletterForm = ({
  campaignLabel,
  className,
  policy,
  sourceID,
  submitText,
  thankYouText,
}: {
  campaignLabel?: string
  className?: string
  policy?: ReactNode
  sourceID?: string
  submitText: string
  thankYouText?: string
}) => {
  const id = React.useId()
  const { t } = useTranslation()
  const { subscribe } = useNewsletterSubscription()

  const form = useForm({
    defaultValues: { email: '' },
    onSubmit: async ({ value }) => {
      const result = await subscribe({ campaignLabel, email: value.email, source: sourceID })
      if (!result) {
        throw new Error('subscription_failed')
      }

      gtm({ event: 'notifyMeCms', moduleKey: campaignLabel })
      ampli.cmsNotifyMeOptIn({ campaign_label: campaignLabel })
    },
  })

  const emailValidator = ({ value }: { value: string }) => {
    if (!value) {
      return t('validation:EMAIL_REQUIRED_TEXT')
    }

    if (!EMAIL_REGEX.test(value)) {
      return t('validation:YOUR_EMAIL_IS_INVALID')
    }

    return undefined
  }

  return (
    <div className={clsx(className, styles.container)}>
      <form.Subscribe selector={state => state.isSubmitSuccessful}>
        {(isSubmitSuccessful) => {
          if (isSubmitSuccessful) {
            return (
              <Subscribed message={thankYouText} />
            )
          }

          return (
            <form
              className={styles.wrapper}
              noValidate
              onSubmit={(e) => {
                e.preventDefault()
                form.handleSubmit()
              }}
            >
              <form.Field
                name="email"
                validators={{
                  onSubmit: emailValidator,
                }}
              >
                {field => (
                  <div>
                    <InputGroup state={metaToFieldState(field.state.meta)}>
                      <InputGroupBase>
                        <InputGroupLabel htmlFor={`${field.name}-${id}`}>
                          {t('footer:FOOTER_SIGN_UP_YOUR_EMAIL')}
                        </InputGroupLabel>
                        <InputGroupInput
                          aria-invalid={field.state.meta.isValid === false && field.state.meta.isTouched}
                          autoComplete="email"
                          id={`${field.name}-${id}`}
                          name={field.name}
                          onBlur={field.handleBlur}
                          onChange={e => field.handleChange(e.target.value)}
                          type="email"
                          value={field.state.value}
                        />
                      </InputGroupBase>
                    </InputGroup>
                    <InputErrors
                      errors={field.state.meta.errors}
                      isTouched={field.state.meta.isTouched}
                      isValid={field.state.meta.isValid}
                    />
                  </div>
                )}
              </form.Field>
              <div>
                <form.Subscribe selector={state => state.isSubmitting}>
                  {isSubmitting => (
                    <Button fillWidth innerSpacing="large" loading={isSubmitting} theme="black" type="submit">
                      {submitText || t('cms:NOTIFY_ME_VIA_EMAIL')}
                    </Button>
                  )}
                </form.Subscribe>
              </div>
              {policy || <UnsubscribePolicy />}
            </form>
          )
        }}
      </form.Subscribe>
    </div>
  )
}
